import { Theme, css, useTheme } from '@emotion/react';

import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';

import { useBundlePass } from '@hooks/useBundlePass';
import { useModal } from '@hooks/useModal';
import { useAutoPurchaseStore } from '@stores/useAutoPurchaseStore';
import { useLoadingStore } from '@stores/useLoadingStore';
import { FontSizes, Spacing } from 'chatie-design-system';

// import { useLiveQuery } from 'dexie-react-hooks';
import HorizontalFilter from '@components/common/HorizontalFilter';
import NativeInFeedAd from '@components/common/NativeInFeedAd';
import { PaginationIterator } from '@components/common/Pagination';
import Pad from '@components/layout/Pad';
import EpisodeSkeleton from '@components/skeleton/EpisodeSkeleton';
import SkeletonList from '@components/skeleton/SkeletonList';
import Episode from '@components/stories/Episode';

import {
  fetchEpisodes,
  previewEpisodeByBalloon,
  purchaseEpisodeByBalloon,
} from '@api/episode';
import {
  CommentObjectType,
  EpisodePurchaseType,
  EpisodeReadFrom,
  EpisodeStatus,
  PAYMENT_VENDOR,
  Platform,
  QUERY_KEYS,
  StoryTheme,
} from '@constants';
import { useGlobalContext } from '@contexts/global';
import { db } from '@database/database.config';
import { IEpisode, IStoryDetail, IWaitfreeStoryDetail } from '@model';
import { sticky } from '@styles/css';
import { useAnalytics } from '@utils/analytics';
import { formatDistance, isAfter, isBefore } from '@utils/date';
import { useHistory, useMe } from '@utils/hook';
import { checkWaitfreeStory, isEnv, toQueryString } from '@utils/misc';
import {
  getRouterQuery,
  useOptimisticUpdate,
  usePaginationQuery,
} from '@utils/query';
import { useWebView } from '@utils/webview';

const PreviewDonationModal = dynamic(
  () =>
    import('@components/modal/DonationModal').then(
      (mod) => mod.PreviewDonationModal
    ),
  { ssr: false }
);

const EpisodePurchaseModal = dynamic(
  () => import('@components/modal/EpisodePurchaseModal'),
  { ssr: false }
);

const HIDDEN_STATS_STORY_IDS = [9817116, 9817120, 9817119, 9817121];

const ADULT_STORY_IDS = isEnv('production')
  ? [
      7091606, 7091607, 7091611, 7091612, 7091613, 7091614, 7091615, 7091616,
      7091617, 7091618, 7091619, 7091620, 7091621, 7091622, 7091623, 7091624,
      7091625, 7091626, 7091627, 7091628, 7091629, 7091630, 7091631, 7091632,
      7091633, 7091634, 7091636, 7091637, 7091638, 7091639, 7091640, 7091641,
      7091642, 7091643, 7091644, 7091646, 7091647, 7091648, 7091649, 7091650,
      7091651, 7091652, 7091653, 7091654, 7091655, 7091656, 7091657, 7091658,
      7091659, 7091660, 7091661, 7091662, 7091663, 7091664, 7091665, 7091666,
      7091667, 7091668, 7091669, 7091670, 7091671, 7091672, 7091673, 7091675,
      7091676, 7091677, 7091678, 7091679, 7091680, 7091681, 7091682, 7091683,
      7091684, 7091685, 7091686, 7091687, 7091688, 7091689, 7091690, 7091691,
      7091692, 7091693, 7091694, 7091695, 7091696, 7091698, 7091699, 7091700,
      7091701, 7091703, 7091704, 7091705, 7091706, 7091707, 7091708, 7091709,
      7091710, 7091711, 7091712, 7091713, 7091714, 7091715, 7091716, 7091717,
      7091718, 7091719, 7091720, 7091721, 7091722, 7091723, 7091724, 7091725,
      7091727, 7091728, 7091729, 7091730, 7091731, 7091732, 7091733, 7091736,
      7091737, 7091738, 7091739, 7091740, 7091742, 7091743, 7091744, 7091747,
      7091748, 7091750, 7091886,
    ]
  : [];

const episodeListHeaderCSS = (theme: Theme) => css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${Spacing.small}px ${Spacing.medium}px;
  background-color: ${theme.var.white};
  border-bottom: 1px solid ${theme.border.lightest};
  z-index: 3;
  ${sticky(44)};

  p {
    font-size: ${FontSizes.small}px;
  }
`;

type EpisodeOrdering = 'newest' | 'oldest';

const EpisodeList = ({ story }: { story: IStoryDetail }) => {
  const { t } = useTranslation();
  const { web } = useWebView();
  const { firebase } = useAnalytics();

  const theme = useTheme();
  const router = useRouter();
  const history = useHistory();
  const storyId = getRouterQuery(router.query.storyId) as number;
  const isExpiredStory = story?.licenseStatus === 'inactive';
  const { platform } = useGlobalContext();
  const vendor =
    platform === Platform.ios ? PAYMENT_VENDOR.apple : PAYMENT_VENDOR.standard;
  const { isPurchaseDailyFreePass, isShowDailyFreePassBanner } = useBundlePass({
    vendor,
  });
  const isShowDailyFreePass =
    isShowDailyFreePassBanner && !isPurchaseDailyFreePass;

  const isPurchaseLoadingRef = useRef(false);

  // const readHistories = useLiveQuery(
  //   () =>
  //     db.readHistory
  //       .where('storyId')
  //       .equals(storyId)
  //       .toArray((items) => items.map((item) => item.episodeId)),
  //   [storyId],
  //   [] as number[]
  // );

  const { setLoading } = useLoadingStore();
  const { autoPurchase } = useAutoPurchaseStore();

  const { data: me } = useMe();
  const [readHistories, setReadHistories] = useState<number[]>([0]);
  const [ordering, setOrdering] = useState<EpisodeOrdering>(
    (getRouterQuery(router.query.ordering) as EpisodeOrdering) ||
      (story.isFinished ? 'oldest' : 'newest')
  );

  const queryKey = QUERY_KEYS.STORY.EPISODE_LIST(storyId, ordering);
  const query = usePaginationQuery(
    queryKey,
    ({ pageParam }) => fetchEpisodes(storyId, ordering, pageParam),
    { suspense: false }
  );

  const queryClient = useQueryClient();
  const { mutate: purchaseEpisode } = useMutation(purchaseEpisodeByBalloon);
  const { mutate: previewEpisode } = useMutation(previewEpisodeByBalloon);

  const optimistic = useOptimisticUpdate();

  /** Modal */
  const [
    isEpisodePurchaseModalOpen,
    { open: openEpisodePurchaseModal, close: closeEpisodePurchaseModal },
    episodeForPurchase,
  ] = useModal<IEpisode>('episode-purchase-modal', { routeChange: false });
  const [
    isPreviewDonationModalOpen,
    { open: openPreviewDonationModal, close: closePreviewDonationModal },
    episodeForPreview,
  ] = useModal<IEpisode>('preview-donation-modal', { routeChange: false });

  const goToEpisode = useCallback(
    (episode: IEpisode) => {
      const url = `/stories/${story.id}/episodes/${episode.id}`;

      if (story.latestRead && episode.id === story.latestRead.episodeId) {
        router.push(
          `${url}${toQueryString({ readFrom: EpisodeReadFrom.list })}`
        );
      } else {
        router.push(url);
      }
    },
    [story]
  );

  const handleEpisodeClick = useCallback(
    (episode: IEpisode) => {
      if (
        isExpiredStory &&
        episode.purchaseType !== EpisodePurchaseType.possession
      ) {
        alert('소장한 회차만 볼 수 있습니다.');
        return;
      }
      if (episode.isFree) {
        goToEpisode(episode);
        return;
      }

      if (!me) {
        if (platform === Platform.web) { router.replace('/signin'); return; }
        alert(t('로그인 후 사용이 가능합니다.'));
        return;
      }
      // 예약 공개 작품
      if (episode.status === EpisodeStatus.bookedPublic) {
        if (isPurchaseDailyFreePass && !episode.purchaseType) {
          handlePreviewEpisodeByStoryPass(episode);
          return;
        }
        // 먼저보기 구매하지 않은 경우
        if (episode.purchaseType !== EpisodePurchaseType.preview) {
          if (isShowDailyFreePass) {
            firebase.logEvent('show_daily_pass_banner', {
              type: 'preview_banner',
            });
          }
          openPreviewDonationModal(episode);
          return;
        }
      }

      // 대여 만료 또는 구매하지 않은 경우
      if (
        !episode.purchaseType || // 구매하지 않았을경우
        (episode.purchaseType === EpisodePurchaseType.rental && //기다무 대여일경우
          episode.purchaseExpired &&
          isBefore(episode.purchaseExpired, new Date())) // 기다무 대여기간 만료일경우
      ) {
        // 기다리면 무료 구매 가능 여부 확인
        if (checkWaitfreeStory(story)) {
          // 기다무 구매 가능할경우
          if (
            !episode.isWaitfreeBlocked &&
            (!story.waitfreeReset || isAfter(new Date(), story.waitfreeReset))
          ) {
            handlePurchaseEpisode(
              episode,
              EpisodePurchaseType.waitfree,
              false,
              false
            );
            return;
          }
        }

        // 자동 구매
        if (autoPurchase && autoPurchase.storyId === story.id) {
          handlePurchaseEpisode(
            episode,
            autoPurchase.purchaseType,
            autoPurchase.isAutoApplyVoucher,
            false
          );
          return;
        }

        if (isShowDailyFreePass) {
          firebase.logEvent('show_daily_pass_banner', {
            type: 'waitfree_banner',
          });
        }
        openEpisodePurchaseModal(episode);

        return;
      }
      if (isPurchaseDailyFreePass) {
        web.showToast('하루프리패스권으로 회차를 열람했어요!');
      }
      goToEpisode(episode);
    },
    [me, story, autoPurchase, isPurchaseDailyFreePass]
  );

  const handleEpisodeCommentClick = useCallback((episode: IEpisode) => {
    router.push(
      `/comments${toQueryString({
        objectType: CommentObjectType.episode,
        objectId: episode.id,
        storyId: story.id,
      })}`
    );
  }, []);

  const handlePurchaseEpisode = useCallback(
    (
      episode: IEpisode,
      purchaseType: EpisodePurchaseType,
      isAutoApplyVoucher?: boolean,
      checkModal = true
    ) => {
      if (isPurchaseLoadingRef.current) {
        return;
      }
      isPurchaseLoadingRef.current = true;
      setLoading(true);

      purchaseEpisode(
        {
          storyId: story.id,
          episodeId: episode.id,
          isAutoApplyVoucher,
          type: purchaseType,
        },
        {
          onSuccess: (data) => {
            if (purchaseType === EpisodePurchaseType.waitfree) {
              web.showToast(t('기다리면 무료로 회차를 열람하였습니다.'));
              const storyQueryKey = QUERY_KEYS.STORY.ONE(storyId);
              optimistic.update<IWaitfreeStoryDetail>(
                storyQueryKey,
                (story) => ({
                  ...story,
                  waitfreeReset: (data?.story as IWaitfreeStoryDetail)
                    ?.waitfreeReset,
                })
              );
            } else if (purchaseType === EpisodePurchaseType.rental) {
              web.showToast(t('회차를 대여하였습니다.'));
            } else if (purchaseType === EpisodePurchaseType.possession) {
              web.showToast(t('회차를 소장하였습니다.'));
            } else if (purchaseType === EpisodePurchaseType.storyPass) {
              web.showToast(t('하루프리패스권으로 회차를 열람했어요!'));
              ['newest', 'oldest'].forEach((ordering) => {
                optimistic.update<IEpisode>(
                  QUERY_KEYS.STORY.EPISODE_LIST(storyId, ordering),
                  {
                    purchaseType: data.episode.purchaseType,
                    purchaseExpired: data.episode.purchaseExpired,
                  },
                  { id: episode.id }
                );
              });
            }

            optimistic.update<IEpisode>(queryKey, data.episode, {
              id: episode.id,
            });
            queryClient.invalidateQueries(['voucherRecommendation']);
            if (checkModal) {
              closeEpisodePurchaseModal({
                afterClose: () => {
                  router.push(`/stories/${storyId}/episodes/${episode.id}`);
                },
              });
            } else {
              router.push(`/stories/${storyId}/episodes/${episode.id}`);
            }
          },
          onSettled: () => {
            isPurchaseLoadingRef.current = false;
            setLoading(false);
          },
        }
      );
    },
    [story]
  );

  const userQueryKey = QUERY_KEYS.USER.ONE(story.user.id);

  const handlePreviewEpisodeByStoryPass = (episode: IEpisode) => {
    previewEpisode(
      {
        storyId: story.id,
        episodeId: episode.id,
        balloon: 200,
        message: t('먼저 보고가요!'),
        useStoryPass: true,
      },
      {
        onSuccess: () => {
          web.showToast('하루프리패스권으로 회차를 열람했어요!');

          optimistic.update(userQueryKey, (user) => ({
            ...user,
            isFan: true,
          }));
          optimistic.update<IEpisode>(
            queryKey,
            { purchaseType: EpisodePurchaseType.preview },
            { id: episode.id }
          );
          goToEpisode(episode);

          // firebase.logEvent('preview_content', {
          //   contentType: 'episode',
          //   storyId: story.id,
          //   episodeId: episode.id,
          //   balloon: values.balloon,
          // });
        },
      }
    );
  };

  const handlePreviewEpisode = useCallback((episode: IEpisode) => {
    closePreviewDonationModal({
      afterClose: () => {
        router.push(`/stories/${storyId}/episodes/${episode.id}`);
      },
    });
    optimistic.update<IEpisode>(
      queryKey,
      { purchaseType: EpisodePurchaseType.preview },
      { id: episode.id }
    );
  }, []);

  const handleChangeOrder = useCallback((ordering: EpisodeOrdering) => {
    setOrdering(ordering);
    history.replaceShallow(
      `/stories/[storyId]${toQueryString({ storyId, ordering })}`,
      `/stories/${storyId}${toQueryString({ ordering })}`
    );
  }, []);

  useEffect(() => {
    if (!('indexedDB' in window) || !db || !window.indexedDB) {
      return;
    }
    if (typeof window !== 'undefined' && storyId && window?.indexedDB) {
      const getReadHistories = async () => {
        try {
          const result = await db?.readHistory
            .where('storyId')
            .equals(storyId)
            .toArray((items) => items.map((item) => item.episodeId));
          setReadHistories(result);
        } catch (err) {
          setReadHistories([]);
        }
      };
      getReadHistories();
    }
  }, [storyId]);

  return (
    <>
      <header css={episodeListHeaderCSS}>
        <p>{t('회차({{number}})', { number: query.totalCount })}</p>

        <HorizontalFilter
          options={[
            {
              label: t('최신순'),
              value: 'newest',
            },
            {
              label: t('첫회차순'),
              value: 'oldest',
            },
          ]}
          selected={ordering}
          onChange={handleChangeOrder}
        />
      </header>

      {query.isLoading ? (
        <SkeletonList skeleton={EpisodeSkeleton} />
      ) : (
        <PaginationIterator name="story-episode-list" query={query} border inFeedAd={<NativeInFeedAd unitKey="episode_mobile" />}>
          {(item) => (
            <Pad
              backgroundColor={
                item.status === EpisodeStatus.review ||
                item.status === EpisodeStatus.reject
                  ? theme.var.gray100
                  : readHistories.indexOf(item.id) > -1
                  ? theme.var.highlight
                  : undefined
              }
            >
              <Episode
                {...item}
                isExpired={isExpiredStory}
                renderPrice={() => {
                  if (story.hasBookedPublic && !isExpiredStory) {
                    if (item.purchaseType === EpisodePurchaseType.preview) {
                      return <small>{t('먼저봄')}</small>;
                    }

                    return null;
                  }

                  // 유료 회차가 있는 경우, 무료 표시
                  if (story.freeEpisodeCount < story.episodeCount) {
                    if (
                      item.purchaseType === EpisodePurchaseType.rental &&
                      item.purchaseExpired &&
                      !isExpiredStory
                    ) {
                      const now = new Date();
                      if (isBefore(item.purchaseExpired, now)) {
                        return null;
                      }

                      return (
                        <small>
                          {t('{{purchaseExpired}} 남음', {
                            purchaseExpired: formatDistance(
                              item.purchaseExpired,
                              now,
                              {
                                addSuffix: false,
                              }
                            ),
                          })}
                        </small>
                      );
                    }

                    if (item.purchaseType === EpisodePurchaseType.possession) {
                      return <small>{t('소장')}</small>;
                    }

                    if (item.isFree && !isExpiredStory) {
                      return <small>{t('무료')}</small>;
                    }
                  }

                  return null;
                }}
                commentEnabled={story.isCommentEnabled && !isExpiredStory}
                hideStats={HIDDEN_STATS_STORY_IDS.includes(story.id)}
                hideDate={
                  isExpiredStory || ADULT_STORY_IDS.indexOf(story.id) > -1
                }
                onClick={handleEpisodeClick}
                onCommentClick={handleEpisodeCommentClick}
              />
            </Pad>
          )}
        </PaginationIterator>
      )}

      <EpisodePurchaseModal
        name="episode-purchase-modal"
        open={isEpisodePurchaseModalOpen}
        story={story}
        episode={episodeForPurchase}
        possessionOnly={story.theme === StoryTheme.tapNovel}
        onClose={closeEpisodePurchaseModal}
        onPurchase={handlePurchaseEpisode}
      />

      <PreviewDonationModal
        name="preview-donation-modal"
        open={isPreviewDonationModalOpen}
        story={story}
        episode={episodeForPreview}
        onClose={closePreviewDonationModal}
        onPreview={handlePreviewEpisode}
      />
    </>
  );
};

export default EpisodeList;
